import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Pagination,
  InputAdornment,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Done as DoneIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import {
  thanksgivingReservationsApi,
  ThanksgivingReservation,
  ConfirmationStatus,
  ReservationType,
} from '../services/thanksgivingReservationsService.ts';
// TEMPORARY: thanksgivingTimingWindowsApi is unused below - the timing windows row
// runs on local-only state until the backend route is deployed to production.
import {
  ThanksgivingTimingWindowEntry,
  TimingWindowOption,
  ReservationStatusOption,
} from '../services/thanksgivingTimingWindowsService.ts';
import { formatPhoneNumber } from '../utils/formatPhone.ts';

const TIMING_WINDOW_OPTIONS: TimingWindowOption[] = ['12 PM to 2 PM', '2 PM to 4 PM', '4 PM to 6 PM'];
const RESERVATION_STATUS_OPTIONS: ReservationStatusOption[] = ['Full', 'Accepting', 'Small Parties'];

const emptyTimingWindowForm = {
  timingWindow: '' as TimingWindowOption | '',
  noOfTables: '',
  noOfPeople: '',
  reservationStatus: 'Accepting' as ReservationStatusOption,
};
type TimingWindowForm = typeof emptyTimingWindowForm;

// Maps a "HH:MM" reservation time to the Timing Window it falls into, so a new
// reservation can be checked against that window's capacity status. Times outside
// all three windows (before noon or after 6 PM) aren't covered by any window.
const TIMING_WINDOW_RANGES: Array<{ window: TimingWindowOption; startMinutes: number; endMinutes: number }> = [
  { window: '12 PM to 2 PM', startMinutes: 12 * 60, endMinutes: 14 * 60 },
  { window: '2 PM to 4 PM', startMinutes: 14 * 60, endMinutes: 16 * 60 },
  { window: '4 PM to 6 PM', startMinutes: 16 * 60, endMinutes: 18 * 60 },
];

const getTimingWindowForTime = (time: string): TimingWindowOption | null => {
  const match = time.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const minutes = parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
  const range = TIMING_WINDOW_RANGES.find((r) => minutes >= r.startMinutes && minutes < r.endMinutes);
  return range ? range.window : null;
};

const getTimingStatusColor = (status: string) => {
  switch (status) {
    case 'Accepting':
      return 'success';
    case 'Small Parties':
      return 'warning';
    case 'Full':
      return 'error';
    default:
      return 'default';
  }
};

const THANKSGIVING_DATE = '2026-11-26';
const STATUS_OPTIONS: ConfirmationStatus[] = ['Pending', 'Confirmed', 'Cancelled'];
const RESERVATION_TYPE_OPTIONS: ReservationType[] = ['Online', 'Opentable', 'Phone', 'In-person'];

// S.No, Date, Time, Name, No. of People, Email, Phone, Table #, Type, Confirmation, Notes, Actions
const THANKSGIVING_TABLE_COLUMN_WIDTHS = ['4%', '8%', '6%', '13%', '7%', '12%', '9%', '6%', '8%', '9%', '11%', '7%'];

const emptyForm = {
  reservationDate: THANKSGIVING_DATE,
  reservationTime: '17:00',
  name: '',
  numberOfPeople: '',
  phone: '',
  email: '',
  tableNumber: '',
  notes: '',
  reservationType: 'Phone' as ReservationType | '',
  confirmed: 'Pending' as ConfirmationStatus,
};

type ReservationFormState = typeof emptyForm;

/** Dates round-trip through the API as full ISO timestamps (date columns serialize
 * with a UTC midnight time); pull out just the calendar date for a date input. */
const toDateInputValue = (dateString: string) => {
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return dateString;
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'Confirmed':
      return 'success';
    case 'Pending':
      return 'warning';
    case 'Cancelled':
      return 'error';
    default:
      return 'default';
  }
};

const formatTime = (time: string) => {
  const match = time.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return time;
  const hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'PM' : 'AM';
  let hours12 = hours % 12;
  if (hours12 === 0) hours12 = 12;
  return `${hours12}:${minutes} ${period}`;
};

const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      timeZone: 'UTC',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
};

const ThanksgivingReservationsPage: React.FC = () => {
  const { sessionToken, user } = useAuth();
  const isAdmin = user?.type === 'admin';
  const { showSuccess, showError } = useSnackbar();

  const [reservations, setReservations] = useState<ThanksgivingReservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  // High enough to show every reservation as one continuous list instead of paging.
  const [limit] = useState(1000);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [totalGuests, setTotalGuests] = useState(0);

  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<ConfirmationStatus | 'all'>('all');

  const [form, setForm] = useState<ReservationFormState>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  // Popup shown when a new reservation's time falls in a Timing Window marked "Full".
  const [fullWindowMessage, setFullWindowMessage] = useState<string | null>(null);

  const [editingReservation, setEditingReservation] = useState<ThanksgivingReservation | null>(null);
  const [editForm, setEditForm] = useState<ReservationFormState>(emptyForm);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>('idle');
  const [autoSaveError, setAutoSaveError] = useState('');
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    };
  }, []);

  // --- Timing Window / capacity row (above New Reservation) ---
  // TEMPORARY: static/local-only until the backend's /thanksgiving-timing-windows
  // route is deployed to production (it 404s there right now). No API calls here -
  // values live only in this component's state and reset on page reload. Swap back
  // to thanksgivingTimingWindowsApi once production has the route.
  const nextLocalTimingWindowId = useRef(1);
  const [timingWindows, setTimingWindows] = useState<ThanksgivingTimingWindowEntry[]>([]);
  const timingWindowsLoading = false;
  const [timingWindowAddForm, setTimingWindowAddForm] = useState<TimingWindowForm>(emptyTimingWindowForm);
  const timingWindowAdding = false;
  const [editingTimingWindowId, setEditingTimingWindowId] = useState<number | null>(null);
  const [timingWindowEditForm, setTimingWindowEditForm] = useState<TimingWindowForm>(emptyTimingWindowForm);
  const timingWindowAutoSaveStatus: 'idle' | 'pending' | 'saving' | 'saved' | 'error' = 'idle';
  const timingWindowAutoSaveError = '';

  const validateTimingWindowForm = (snapshot: TimingWindowForm, onInvalid: (message: string) => void) => {
    if (!snapshot.timingWindow || snapshot.noOfTables === '' || snapshot.noOfPeople === '') {
      onInvalid('Please fill in Timing Window, No. of Tables, and No. of People.');
      return null;
    }
    const noOfTables = parseInt(snapshot.noOfTables, 10);
    if (isNaN(noOfTables) || noOfTables < 0) {
      onInvalid('No. of Tables must be a valid number.');
      return null;
    }
    const noOfPeople = parseInt(snapshot.noOfPeople, 10);
    if (isNaN(noOfPeople) || noOfPeople < 0) {
      onInvalid('No. of People must be a valid number.');
      return null;
    }
    return {
      timingWindow: snapshot.timingWindow as TimingWindowOption,
      noOfTables,
      noOfPeople,
      reservationStatus: snapshot.reservationStatus,
    };
  };

  const handleTimingWindowAddFormChange = (field: keyof TimingWindowForm, value: string) => {
    setTimingWindowAddForm((prev) => ({ ...prev, [field]: value } as TimingWindowForm));
  };

  const handleAddTimingWindow = () => {
    const parsed = validateTimingWindowForm(timingWindowAddForm, showError);
    if (!parsed) return;

    const created: ThanksgivingTimingWindowEntry = {
      id: nextLocalTimingWindowId.current++,
      ...parsed,
      userId: 0,
      createdDate: Date.now(),
    };
    setTimingWindows((prev) => [...prev, created]);
    setTimingWindowAddForm(emptyTimingWindowForm);
  };

  const startEditTimingWindow = (entry: ThanksgivingTimingWindowEntry) => {
    setEditingTimingWindowId(entry.id);
    setTimingWindowEditForm({
      timingWindow: entry.timingWindow,
      noOfTables: entry.noOfTables.toString(),
      noOfPeople: entry.noOfPeople.toString(),
      reservationStatus: entry.reservationStatus,
    });
  };

  const handleTimingWindowEditFormChange = (field: keyof TimingWindowForm, value: string) => {
    setTimingWindowEditForm((prev) => {
      const next = { ...prev, [field]: value } as TimingWindowForm;
      if (editingTimingWindowId !== null) {
        const parsed = validateTimingWindowForm(next, () => {});
        if (parsed) {
          setTimingWindows((prevRows) => prevRows.map((e) => (e.id === editingTimingWindowId ? { ...e, ...parsed } : e)));
        }
      }
      return next;
    });
  };

  const finishEditTimingWindow = () => {
    setEditingTimingWindowId(null);
  };

  const handleDeleteTimingWindow = (id: number) => {
    if (!isAdmin) return;
    if (!window.confirm('Delete this timing window?')) return;
    setTimingWindows((prev) => prev.filter((e) => e.id !== id));
    if (editingTimingWindowId === id) setEditingTimingWindowId(null);
  };

  // The backend has no server-side search/filtering (only page/limit), so name/status
  // matching is done client-side below, over whichever page is currently loaded.
  const fetchReservations = useCallback(
    async (pageNum: number = 1) => {
      if (!sessionToken) return;
      setLoading(true);
      try {
        const response = await thanksgivingReservationsApi.getReservations({ page: pageNum, limit }, sessionToken);
        setReservations(response.data);
        setPage(response.page);
        setTotalPages(response.totalPages);
        setTotal(response.total);
        setTotalGuests(response.data.reduce((sum, r) => sum + (r.numberOfPeople || 0), 0));
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to fetch reservations';
        showError(message);
      } finally {
        setLoading(false);
      }
    },
    [sessionToken, limit, showError]
  );

  useEffect(() => {
    fetchReservations(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToken]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    fetchReservations(value);
  };

  const handleClearSearch = () => {
    setSearchText('');
    setStatusFilter('all');
  };

  const visibleReservations = reservations.filter((r) => {
    if (statusFilter !== 'all' && r.confirmed !== statusFilter) return false;
    if (!searchText) return true;
    const q = searchText.trim().toLowerCase();
    const haystack = `${r.name} ${r.phone ?? ''} ${r.email ?? ''} ${r.tableNumber ?? ''}`.toLowerCase();
    return haystack.includes(q);
  });

  const totalVisibleGuests = visibleReservations.reduce((sum, r) => sum + (r.numberOfPeople || 0), 0);

  const handleFormChange = (field: keyof ReservationFormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreate = async () => {
    if (!sessionToken) return;

    const people = parseInt(form.numberOfPeople, 10);
    if (!form.reservationDate || !form.reservationTime || !form.name.trim() || !people || people < 1) {
      showError('Please fill in date, time, name, and party size.');
      return;
    }

    const matchedWindow = getTimingWindowForTime(form.reservationTime);
    const windowEntry = matchedWindow ? timingWindows.find((w) => w.timingWindow === matchedWindow) : undefined;
    if (windowEntry?.reservationStatus === 'Full') {
      setFullWindowMessage(`Reservations between ${matchedWindow} are full. Please check other slots.`);
      return;
    }

    setSubmitting(true);
    try {
      await thanksgivingReservationsApi.createReservation(
        {
          reservationDate: form.reservationDate,
          reservationTime: form.reservationTime,
          name: form.name.trim(),
          numberOfPeople: people,
          phone: form.phone.trim() || null,
          email: form.email.trim() || null,
          tableNumber: form.tableNumber.trim() || null,
          notes: form.notes.trim() || null,
          reservationType: form.reservationType || null,
          confirmed: form.confirmed,
        },
        sessionToken
      );
      showSuccess('Reservation added.');
      setForm({ ...emptyForm, reservationDate: form.reservationDate, reservationTime: form.reservationTime });
      fetchReservations(1);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add reservation';
      showError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (reservation: ThanksgivingReservation, confirmed: ConfirmationStatus) => {
    if (!sessionToken) return;
    setReservations((prev) =>
      prev.map((r) => (r.id === reservation.id ? { ...r, confirmed } : r))
    );
    try {
      await thanksgivingReservationsApi.updateReservation(reservation.id, { confirmed }, sessionToken);
    } catch (err) {
      setReservations((prev) =>
        prev.map((r) => (r.id === reservation.id ? { ...r, confirmed: reservation.confirmed } : r))
      );
      const message = err instanceof Error ? err.message : 'Failed to update status';
      showError(message);
    }
  };

  const handleDelete = async (reservation: ThanksgivingReservation) => {
    if (!sessionToken || !isAdmin) return;
    if (!window.confirm(`Remove the reservation for ${reservation.name}?`)) return;
    try {
      await thanksgivingReservationsApi.deleteReservation(reservation.id, sessionToken);
      showSuccess('Reservation removed.');
      fetchReservations(page);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove reservation';
      showError(message);
    }
  };

  const handleOpenEdit = (reservation: ThanksgivingReservation) => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
    setAutoSaveStatus('idle');
    setAutoSaveError('');
    setEditingReservation(reservation);
    setEditForm({
      reservationDate: toDateInputValue(reservation.reservationDate),
      reservationTime: reservation.reservationTime,
      name: reservation.name,
      numberOfPeople: String(reservation.numberOfPeople),
      phone: reservation.phone || '',
      email: reservation.email || '',
      tableNumber: reservation.tableNumber || '',
      notes: reservation.notes || '',
      reservationType: reservation.reservationType || '',
      confirmed: reservation.confirmed,
    });
  };

  // Auto-saves ~900ms after the last change to any field. Validation failures and API
  // errors surface as a status message instead of a toast, since this runs unattended.
  const performAutoSave = useCallback(
    async (reservationId: number, snapshot: ReservationFormState) => {
      if (!sessionToken) return;

      const people = parseInt(snapshot.numberOfPeople, 10);
      if (!snapshot.reservationDate || !snapshot.reservationTime || !snapshot.name.trim() || !people || people < 1) {
        setAutoSaveStatus('error');
        setAutoSaveError('Date, time, name, and party size are required.');
        return;
      }

      setAutoSaveStatus('saving');
      try {
        const updated = await thanksgivingReservationsApi.updateReservation(
          reservationId,
          {
            reservationDate: snapshot.reservationDate,
            reservationTime: snapshot.reservationTime,
            name: snapshot.name.trim(),
            numberOfPeople: people,
            phone: snapshot.phone.trim() || null,
            email: snapshot.email.trim() || null,
            tableNumber: snapshot.tableNumber.trim() || null,
            notes: snapshot.notes.trim() || null,
            reservationType: snapshot.reservationType || null,
            confirmed: snapshot.confirmed,
          },
          sessionToken
        );
        setReservations((prev) => prev.map((r) => (r.id === reservationId ? updated : r)));
        setAutoSaveStatus('saved');
      } catch (err) {
        setAutoSaveStatus('error');
        setAutoSaveError(err instanceof Error ? err.message : 'Failed to save changes');
      }
    },
    [sessionToken]
  );

  const handleCloseEdit = async () => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
      if (editingReservation) {
        await performAutoSave(editingReservation.id, editForm);
      }
    }
    setEditingReservation(null);
  };

  const handleEditFormChange = (field: keyof ReservationFormState, value: string) => {
    if (!editingReservation) return;
    const reservationId = editingReservation.id;

    setEditForm((prev) => {
      const next = { ...prev, [field]: value };

      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
      setAutoSaveStatus('pending');
      autoSaveTimeoutRef.current = setTimeout(() => {
        autoSaveTimeoutRef.current = null;
        performAutoSave(reservationId, next);
      }, 900);

      return next;
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ color: '#7f0000', fontWeight: 700, flex: '0 0 auto', whiteSpace: 'nowrap' }}>
          Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
            Thanksgiving Reservations
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#800000' }}>
            {total} reservation{total !== 1 ? 's' : ''} on this page &middot; {totalGuests} guest
            {totalGuests !== 1 ? 's' : ''} seated
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => fetchReservations(page)}>
          Refresh
        </Button>
      </Box>

      {/* Timing Window / capacity row */}
      <Paper sx={{ width: '100%', overflow: 'hidden', border: '2px solid #7f0000', mb: 3 }}>
        <TableContainer>
          <Table sx={{ borderCollapse: 'collapse', '& td': { border: '2px solid #9e9e9e', py: 0.75, px: 1.5 } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#b71c1c' }}>
                {['Timing Window', 'No. of Tables', 'No. of People', 'Reservation', 'Actions'].map((label) => (
                  <TableCell
                    key={label}
                    align={label === 'Actions' ? 'center' : label === 'Timing Window' ? 'left' : 'right'}
                    sx={{
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '0.78rem',
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      border: '2px solid #7f0000',
                      py: 1,
                      px: 1.5,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {timingWindowsLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={22} />
                  </TableCell>
                </TableRow>
              ) : (
                timingWindows.map((entry) => {
                  const isEditing = editingTimingWindowId === entry.id;
                  return (
                    <TableRow key={entry.id} hover>
                      <TableCell>
                        {isEditing ? (
                          <FormControl size="small" fullWidth>
                            <Select
                              value={timingWindowEditForm.timingWindow}
                              onChange={(e) => handleTimingWindowEditFormChange('timingWindow', e.target.value as string)}
                            >
                              {TIMING_WINDOW_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          entry.timingWindow
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isEditing ? (
                          <TextField
                            size="small"
                            type="number"
                            inputProps={{ min: 0 }}
                            value={timingWindowEditForm.noOfTables}
                            onChange={(e) => handleTimingWindowEditFormChange('noOfTables', e.target.value)}
                            fullWidth
                          />
                        ) : (
                          entry.noOfTables
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isEditing ? (
                          <TextField
                            size="small"
                            type="number"
                            inputProps={{ min: 0 }}
                            value={timingWindowEditForm.noOfPeople}
                            onChange={(e) => handleTimingWindowEditFormChange('noOfPeople', e.target.value)}
                            fullWidth
                          />
                        ) : (
                          entry.noOfPeople
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isEditing ? (
                          <FormControl size="small" fullWidth>
                            <Select
                              value={timingWindowEditForm.reservationStatus}
                              onChange={(e) => handleTimingWindowEditFormChange('reservationStatus', e.target.value as string)}
                            >
                              {RESERVATION_STATUS_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Chip label={entry.reservationStatus} color={getTimingStatusColor(entry.reservationStatus) as any} size="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {isEditing ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                            <IconButton size="small" color="success" onClick={finishEditTimingWindow} disabled={timingWindowAutoSaveStatus === 'saving'}>
                              <DoneIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="caption" color={timingWindowAutoSaveStatus === 'error' ? 'error' : 'text.secondary'}>
                              {timingWindowAutoSaveStatus === 'saving' && 'Saving…'}
                              {timingWindowAutoSaveStatus === 'pending' && 'Unsaved…'}
                              {timingWindowAutoSaveStatus === 'saved' && 'Saved'}
                              {timingWindowAutoSaveStatus === 'error' && (timingWindowAutoSaveError || 'Error')}
                            </Typography>
                          </Box>
                        ) : (
                          <>
                            <IconButton size="small" color="primary" onClick={() => startEditTimingWindow(entry)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            {isAdmin && (
                              <IconButton size="small" color="error" onClick={() => handleDeleteTimingWindow(entry.id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}

              {/* Blank add-row */}
              <TableRow sx={{ '& td': { backgroundColor: '#fafafa' } }}>
                <TableCell>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={timingWindowAddForm.timingWindow}
                      displayEmpty
                      onChange={(e) => handleTimingWindowAddFormChange('timingWindow', e.target.value as string)}
                    >
                      <MenuItem value=""><em>Select…</em></MenuItem>
                      {TIMING_WINDOW_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{ min: 0 }}
                    value={timingWindowAddForm.noOfTables}
                    onChange={(e) => handleTimingWindowAddFormChange('noOfTables', e.target.value)}
                    fullWidth
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{ min: 0 }}
                    value={timingWindowAddForm.noOfPeople}
                    onChange={(e) => handleTimingWindowAddFormChange('noOfPeople', e.target.value)}
                    fullWidth
                  />
                </TableCell>
                <TableCell align="right">
                  <FormControl size="small" fullWidth>
                    <Select
                      value={timingWindowAddForm.reservationStatus}
                      onChange={(e) => handleTimingWindowAddFormChange('reservationStatus', e.target.value as string)}
                    >
                      {RESERVATION_STATUS_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" color="success" onClick={handleAddTimingWindow} disabled={timingWindowAdding}>
                    {timingWindowAdding ? <CircularProgress size={18} /> : <AddIcon fontSize="small" />}
                  </IconButton>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add reservation form */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          New Reservation
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 1.5, alignItems: 'flex-start', overflowX: 'auto', pb: 1 }}>
          <TextField
            label="Date"
            type="date"
            value={form.reservationDate}
            onChange={(e) => handleFormChange('reservationDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: '1 1 130px', minWidth: 130 }}
          />
          <TextField
            label="Time"
            type="time"
            value={form.reservationTime}
            onChange={(e) => handleFormChange('reservationTime', e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: '1 1 110px', minWidth: 110 }}
          />
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => handleFormChange('name', e.target.value)}
            sx={{ flex: '2 1 160px', minWidth: 140 }}
          />
          <TextField
            label="No. of people"
            type="number"
            inputProps={{ min: 1, max: 100 }}
            value={form.numberOfPeople}
            onChange={(e) => handleFormChange('numberOfPeople', e.target.value)}
            sx={{ flex: '1 1 90px', minWidth: 90 }}
          />
          <TextField
            label="Phone (optional)"
            value={form.phone}
            onChange={(e) => handleFormChange('phone', e.target.value)}
            placeholder="(555) 555-0123"
            sx={{ flex: '1 1 140px', minWidth: 130 }}
          />
          <TextField
            label="Table #"
            value={form.tableNumber}
            onChange={(e) => handleFormChange('tableNumber', e.target.value)}
            placeholder="12"
            sx={{ flex: '1 1 80px', minWidth: 80 }}
          />
          <TextField
            label="Email (optional)"
            type="email"
            value={form.email}
            onChange={(e) => handleFormChange('email', e.target.value)}
            sx={{ flex: '2 1 160px', minWidth: 150 }}
          />
          <FormControl sx={{ flex: '1 1 130px', minWidth: 130 }}>
            <InputLabel>Reservation Type</InputLabel>
            <Select
              value={form.reservationType}
              label="Reservation Type"
              onChange={(e) => handleFormChange('reservationType', e.target.value as ReservationType)}
            >
              {RESERVATION_TYPE_OPTIONS.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Notes (optional)"
            value={form.notes}
            onChange={(e) => handleFormChange('notes', e.target.value)}
            sx={{ flex: '2 1 180px', minWidth: 160 }}
          />
          <FormControl sx={{ flex: '1 1 130px', minWidth: 130 }}>
            <InputLabel>Confirmation</InputLabel>
            <Select
              value={form.confirmed}
              label="Confirmation"
              onChange={(e) => handleFormChange('confirmed', e.target.value as ConfirmationStatus)}
            >
              {STATUS_OPTIONS.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
            onClick={handleCreate}
            disabled={submitting}
            sx={{ flex: '0 0 auto', alignSelf: 'center', whiteSpace: 'nowrap' }}
          >
            Add Reservation
          </Button>
        </Box>
      </Paper>

      {/* Search / filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            label="Search"
            placeholder="Name, phone, email, or table…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220 }}
          />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Confirmation</InputLabel>
            <Select
              label="Confirmation"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ConfirmationStatus | 'all')}
            >
              <MenuItem value="all">All statuses</MenuItem>
              {STATUS_OPTIONS.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {(searchText || statusFilter !== 'all') && (
            <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleClearSearch}>
              Clear
            </Button>
          )}
        </Box>
      </Paper>

      {/* Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', border: '2px solid #7f0000' }}>
        {loading && reservations.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small" sx={{ borderCollapse: 'collapse', tableLayout: 'fixed', '& td': { border: '2px solid #9e9e9e', py: 0.4, px: 1, fontSize: '0.8rem' } }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#b71c1c' }}>
                    {['S.No', 'Date', 'Time', 'Name', 'No. of People', 'Email', 'Phone', 'Table #', 'Reservation Type', 'Confirmation', 'Notes', 'Actions'].map((label, i) => (
                      <TableCell
                        key={label}
                        align={label === 'No. of People' || label === 'Actions' ? 'center' : 'left'}
                        sx={{
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '0.74rem',
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          border: '2px solid #7f0000',
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          px: 1,
                          width: THANKSGIVING_TABLE_COLUMN_WIDTHS[i],
                        }}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleReservations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          {reservations.length === 0
                            ? 'No reservations yet — add the first one above.'
                            : 'No reservations match your search.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleReservations.map((r, index) => (
                      <TableRow key={r.id} hover>
                        <TableCell sx={{ width: THANKSGIVING_TABLE_COLUMN_WIDTHS[0] }}>{index + 1}</TableCell>
                        <TableCell sx={{ width: THANKSGIVING_TABLE_COLUMN_WIDTHS[1] }}>{formatDate(r.reservationDate)}</TableCell>
                        <TableCell sx={{ width: THANKSGIVING_TABLE_COLUMN_WIDTHS[2] }}>{formatTime(r.reservationTime)}</TableCell>
                        <TableCell sx={{ width: THANKSGIVING_TABLE_COLUMN_WIDTHS[3], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</TableCell>
                        <TableCell align="center" sx={{ width: THANKSGIVING_TABLE_COLUMN_WIDTHS[4] }}>{r.numberOfPeople}</TableCell>
                        <TableCell sx={{ width: THANKSGIVING_TABLE_COLUMN_WIDTHS[5], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.email || '-'}</TableCell>
                        <TableCell sx={{ width: THANKSGIVING_TABLE_COLUMN_WIDTHS[6] }}>{formatPhoneNumber(r.phone) || '-'}</TableCell>
                        <TableCell sx={{ width: THANKSGIVING_TABLE_COLUMN_WIDTHS[7] }}>{r.tableNumber || '-'}</TableCell>
                        <TableCell sx={{ width: THANKSGIVING_TABLE_COLUMN_WIDTHS[8] }}>{r.reservationType || '-'}</TableCell>
                        <TableCell sx={{ width: THANKSGIVING_TABLE_COLUMN_WIDTHS[9] }}>
                          <FormControl size="small" variant="standard" sx={{ minWidth: 120 }}>
                            <Select
                              value={r.confirmed}
                              onChange={(e) => handleStatusChange(r, e.target.value as ConfirmationStatus)}
                              disableUnderline
                              renderValue={(value) => (
                                <Chip label={value} color={getStatusColor(value) as any} size="small" />
                              )}
                            >
                              {STATUS_OPTIONS.map((status) => (
                                <MenuItem key={status} value={status}>
                                  {status}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell
                          title={r.notes || undefined}
                          sx={{ width: THANKSGIVING_TABLE_COLUMN_WIDTHS[10], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {r.notes || '-'}
                        </TableCell>
                        <TableCell align="center" sx={{ width: THANKSGIVING_TABLE_COLUMN_WIDTHS[11] }}>
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => handleOpenEdit(r)}
                            aria-label="edit reservation"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          {isAdmin && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(r)}
                              aria-label="remove reservation"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                  {visibleReservations.length > 0 && (
                    <TableRow sx={{ backgroundColor: '#fde8e8' }}>
                      <TableCell colSpan={4} align="right" sx={{ fontWeight: 700, color: '#7f0000', border: '2px solid #7f0000' }}>
                        Total
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#7f0000', border: '2px solid #7f0000' }}>
                        {totalVisibleGuests}
                      </TableCell>
                      <TableCell colSpan={7} sx={{ border: '2px solid #7f0000' }} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider />

            {totalPages > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Showing {reservations.length} of {total} reservation{total !== 1 ? 's' : ''}
                </Typography>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Edit reservation dialog */}
      <Dialog open={editingReservation !== null} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Reservation</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={editForm.reservationDate}
                onChange={(e) => handleEditFormChange('reservationDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Time"
                type="time"
                value={editForm.reservationTime}
                onChange={(e) => handleEditFormChange('reservationTime', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Name"
                value={editForm.name}
                onChange={(e) => handleEditFormChange('name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="No. of people"
                type="number"
                inputProps={{ min: 1, max: 100 }}
                value={editForm.numberOfPeople}
                onChange={(e) => handleEditFormChange('numberOfPeople', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone (optional)"
                value={editForm.phone}
                onChange={(e) => handleEditFormChange('phone', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Table #"
                value={editForm.tableNumber}
                onChange={(e) => handleEditFormChange('tableNumber', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email (optional)"
                type="email"
                value={editForm.email}
                onChange={(e) => handleEditFormChange('email', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Reservation Type</InputLabel>
                <Select
                  value={editForm.reservationType}
                  label="Reservation Type"
                  onChange={(e) => handleEditFormChange('reservationType', e.target.value as ReservationType)}
                >
                  {RESERVATION_TYPE_OPTIONS.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes (optional)"
                value={editForm.notes}
                onChange={(e) => handleEditFormChange('notes', e.target.value)}
                multiline
                minRows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Confirmation</InputLabel>
                <Select
                  value={editForm.confirmed}
                  label="Confirmation"
                  onChange={(e) => handleEditFormChange('confirmed', e.target.value as ConfirmationStatus)}
                >
                  {STATUS_OPTIONS.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Typography
            variant="body2"
            color={autoSaveStatus === 'error' ? 'error' : 'text.secondary'}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            {autoSaveStatus === 'saving' && (
              <>
                <CircularProgress size={14} />
                Saving…
              </>
            )}
            {autoSaveStatus === 'pending' && 'Unsaved changes…'}
            {autoSaveStatus === 'saved' && 'All changes saved'}
            {autoSaveStatus === 'error' && (autoSaveError || 'Failed to save changes')}
          </Typography>
          <Button onClick={handleCloseEdit} variant="contained" disabled={autoSaveStatus === 'saving'}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Timing window full popup */}
      <Dialog open={fullWindowMessage !== null} onClose={() => setFullWindowMessage(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#7f0000', fontWeight: 700 }}>Timing Window Full</DialogTitle>
        <DialogContent>
          <Typography variant="body1">{fullWindowMessage}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFullWindowMessage(null)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ThanksgivingReservationsPage;
